// The Space card (Space rules spec, 2026-10-29; behind `symphonySpaceCardV2`): the card at the top of a Space, which
// replaces the site strip (`presenceStrip`) when the toggle is on. Every decision is here, pure and pinned by the shared
// vectors (`native-apps/shared/space-card/contract.json`), which web (`test/space-card.test.ts`), iOS (`SpaceCard.swift`)
// and Android (`SpaceCard.kt`) each run — change one, change all three.
//
// What the card is, top to bottom (the prototype as revised 2026-11-03):
//   - identity: the Space's badge (a social card: the shown profile's network mark), the site's (or the shown profile's,
//     or the Space's) name and its address, and — beside them — "View Site" / "Preview" as a quiet eye link (`view`);
//   - chips: the Space's social profiles, the first three, then "+N" (opens Manage Online Presence) and "+ Add". Across
//     the top of a social-only card they are the networks' names and PICK which profile the card shows (`selected`);
//     any other card carries them as its "Connected accounts" row, each with its handle (and followers);
//   - left: the dark "Manage Online Presence" button first (`manage`; never on a Personal card), then equal OUTLINED
//     buttons (Edit Site ↗, or the platform's own). No ⋯ on the card;
//   - right: rows. On a Wix site the Visits panel comes first (the strongest thing on the card), then quiet rows (grey
//     value, text link). No Wix plan row and no Symphony plan row: the Wix plan lives only in the Manage window.
//     A Shopify store's orders of the last 30 days, and a social profile's followers and posts, are counts in that same
//     quiet row; a social profile's Engagement row links to the network's own insights;
//   - at most ONE green button, and only for a real call to action of that card (Publish Site, Migrate Site to Symphony,
//     Build a Site in Symphony) — never a "next step" prompt. A WordPress site's is the `migrate` block: one line of
//     why, then the button, with no row label;
//   - footer: one quiet line, "Expand your online reach: Connect Instagram, Facebook or Google Business →", opening
//     Connectors. Never on a Personal card.
// A row with no data is not drawn (no empty state).

import { presenceLinks, type PresenceGlyph, type PresenceLink } from './presence';
import {
  migrationState,
  presenceIsWix,
  stripEditAction,
  type PresenceContext,
  type PresenceTarget,
  type StripAction,
  type StripCell,
  type WebsitePresence,
} from './presence-checklist';
import { stripVisits, wireNumber } from './site-analytics';
import type { I18nCopy, SpaceBindingsResponse } from './space-resources';
import type { WorkspaceWebsite } from './workspace-website';

const P = 'presence.';
const C = 'spaceCard.';

/** How many social chips the card draws before "+N". */
export const SPACE_CARD_CHIP_LIMIT = 3;

export type SpaceCardVariant =
  /** A published Wix site. */
  | 'wix'
  /** A Wix site that is not live yet (a PUBLISH step not DONE). */
  | 'wixUnpublished'
  | 'shopify'
  | 'wordpress'
  /** Squarespace, Webflow, another platform. */
  | 'external'
  /** No website, at least one social profile. */
  | 'social'
  /** A business Space with no website and no profile yet (the server sent a `presence` with a BUILD step to do). */
  | 'noWebsite'
  /** No website, no profile, no `presence`: Personal. No Manage button and no footer. */
  | 'personal';

/**
 * Where a card action goes: a `PresenceTarget`, or one of the card's own destinations — Connectors (the footer, and
 * Shopify's connection row; `connector` points the page at an entry), the Space's Apps or Calendar page, or the Manage
 * Online Presence window (`add`: with its "+ Add Site, Page or Listing" field open).
 */
export type SpaceCardTarget =
  | PresenceTarget
  | { kind: 'connectors'; connector: string }
  | { kind: 'apps' }
  | { kind: 'calendar' }
  | { kind: 'manage'; add: boolean };

export interface SpaceCardAction {
  label: I18nCopy;
  target: SpaceCardTarget;
  bi: string;
  /** The card's one green button. */
  primary: boolean;
}

export type SpaceCardRowKind =
  | 'visits'
  | 'site'
  | 'domain'
  | 'email'
  | 'orders'
  | 'followers'
  | 'posts'
  | 'engagement'
  | 'connection'
  | 'rebuild'
  /** The WordPress card's call to move: drawn as a block (its value as one line, then the green button), no label. */
  | 'migrate'
  | 'website'
  | 'apps'
  | 'calendar'
  | 'accounts';

export interface SpaceCardVisits {
  /** `known`: the number; `unknown`: the read failed ("—"); `unpublished`: "Publish your site to see visits". */
  state: 'known' | 'unknown' | 'unpublished';
  count: number;
  trend: StripCell['trend'];
  /** The last 30 days, oldest first (the small line); [] for none. */
  daily: number[];
}

export interface SpaceCardRow {
  kind: SpaceCardRowKind;
  label: I18nCopy;
  /**
   * The quiet grey value, or a count the surface formats in its locale and draws in the primary ink (`atLeast`: the
   * source stopped counting there, drawn "10K+"), or `unknown`: the read failed, drawn "—" and never 0; null = none.
   */
  value: I18nCopy | { text: string } | SpaceCardCount | { unknown: true } | null;
  /** A text link, or — `primary` — the card's one green button. */
  action: SpaceCardAction | null;
  /** `visits` only: the tinted panel's figures. */
  visits: SpaceCardVisits | null;
  /** `accounts` only: the Space's social profiles as chips. */
  chips: SpaceCardChips | null;
}

/** A count in a row. `atLeast` is present only when true. */
export interface SpaceCardCount {
  count: number;
  atLeast?: true;
}

/**
 * The first three profiles, then how many more ("+N", which opens Manage Online Presence); "+ Add" is always offered.
 * `selected`: across the top of a social card, the chip of the profile the card shows (drawn pressed); null on the
 * "Connected accounts" row, where a chip opens its profile.
 */
export interface SpaceCardChips {
  shown: SpaceCardChip[];
  more: number;
  selected: string | null;
}

/**
 * What the card shows about the ACCOUNT on a Space with no website and no profile (Personal): the signed-in email under
 * the name, how many apps are connected, and whether a calendar is. A null figure was not read: its row keeps its link
 * and shows no value.
 */
export interface SpaceCardAccount {
  email: string;
  connectedApps: number | null;
  calendarConnected: boolean | null;
  /** The Calendar release (`symphonyCalendar`) is open for this account. Closed, there is no Calendar row: the page
   *  would only redirect home. */
  calendarEnabled: boolean;
}

export interface SpaceCardChip {
  id: string;
  glyph: PresenceGlyph;
  /** The profile's handle or page name, which the "Connected accounts" row draws. */
  name: string;
  /** The network ("Instagram"), which the chips across a social card draw; '' for a plain link (then `name`). */
  network: string;
  /** The public profile; '' = none (a tap opens Manage Online Presence instead). */
  url: string;
  /** The profile's followers, drawn after its name in the "Connected accounts" row; null = not read. */
  followers: number | null;
}

/**
 * What a social profile's binding says about its audience (symphony-bff `SpaceResource.stats`, set on every
 * SOCIAL_PROFILE row): `known: false` = no number — the network gives none (Instagram, TikTok), or the read failed; the
 * server does not tell the two apart. `postsKnown` / `posts30d` the same for its posts of the last 30 days
 * (wix-vmr-repo#35476). Absent from an older server.
 */
export interface SpaceProfileStats {
  known?: boolean;
  followers?: number | string;
  postsKnown?: boolean;
  posts30d?: number | string;
}

/** A social profile as the card reads it: its header link, and its binding's stats. */
export type SpaceCardProfile = PresenceLink & { stats?: SpaceProfileStats };

export interface SpaceCard {
  variant: SpaceCardVariant;
  name: string;
  /** The line under the name: the site's address ("wixsite.com/nettalolo"), "WordPress detected", the profile's
   *  network, the account's email; null for none. */
  sub: I18nCopy | { text: string } | null;
  /** A social card's badge: the shown profile's network mark (on the accent tint); null = the Space's own badge. */
  glyph: PresenceGlyph | null;
  /** The chips across the top — a social-only card only; any other card carries them as its `accounts` row. */
  chips: SpaceCardChips | null;
  /** "View Site" / "Preview": the quiet eye link beside the name; null for none. */
  view: SpaceCardAction | null;
  /** The equal outlined buttons on the left, after Manage, in order. */
  buttons: SpaceCardAction[];
  /** The dark "Manage Online Presence" button, first in the actions. */
  manage: boolean;
  rows: SpaceCardRow[];
  /** The connectors footer line. */
  footer: boolean;
}

/** Only an http(s) address is ever opened. */
function href(url: string | undefined): string {
  const raw = (url ?? '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? raw : '';
  } catch {
    return '';
  }
}

/** A url as the card writes it under the name: no scheme, no `www.`, no trailing slash ("wixsite.com/nettalolo"). */
function address(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '');
}

function copy(key: string, values?: Record<string, string>): I18nCopy {
  return values ? { key, values } : { key };
}

function act(label: I18nCopy, target: SpaceCardTarget, bi: string, primary = false): SpaceCardAction {
  return { label, target, bi, primary };
}

function urlAction(label: I18nCopy, url: string, bi: string): SpaceCardAction | null {
  const u = href(url);
  return u ? act(label, { kind: 'url', url: u }, bi) : null;
}

function row(kind: SpaceCardRowKind, label: I18nCopy, value: SpaceCardRow['value'], action: SpaceCardAction | null): SpaceCardRow {
  return { kind, label, value, action, visits: null, chips: null };
}

function countOf(n: unknown, atLeast = false): SpaceCardCount {
  const count = Math.max(0, Math.floor(wireNumber(n)));
  return atLeast ? { count, atLeast: true } : { count };
}

/** A profile's followers, or null when they were not read. */
function followersOf(stats: SpaceProfileStats | undefined): number | null {
  return stats?.known === true ? countOf(stats.followers).count : null;
}

/** The card's own (Title Case) words for the strip's Edit labels; a platform's admin ("Open Squarespace admin") keeps its own. */
const CARD_EDIT_LABELS: Record<string, string> = {
  [`${P}action.editSite`]: `${C}action.editSite`,
  'siteReview.action.review': `${C}action.reviewSite`,
};

function fromStrip(a: StripAction | null): SpaceCardAction | null {
  if (!a) return null;
  const key = CARD_EDIT_LABELS[a.label.key];
  return { label: key ? { ...a.label, key } : a.label, target: a.target, bi: a.bi.replace('presence_strip_', 'space_card_'), primary: false };
}

function stepState(presence: WebsitePresence, kind: string): string | null {
  const step = (presence.steps ?? []).find((s) => s.kind === kind);
  return step?.state ?? null;
}

function stepUrl(presence: WebsitePresence, kind: string): string {
  return href((presence.steps ?? []).find((s) => s.kind === kind)?.url);
}

function siteName(presence: WebsitePresence, ctx: PresenceContext): string {
  return (presence.displayName ?? '').trim() || ctx.spaceName.trim();
}

function siteRef(presence: WebsitePresence, ctx: PresenceContext): string {
  return ctx.siteUrl.trim() || href(presence.links?.viewUrl) || (presence.displayHost ?? '').trim() || siteName(presence, ctx);
}

/** Which card a Space gets. `presence` null with no profile is Personal; with a website but no `presence`, null. */
export function spaceCardVariant(presence: WebsitePresence | null | undefined, profiles: number, hasWebsite: boolean): SpaceCardVariant | null {
  const noWebsite = !presence || (presence.steps ?? []).some((s) => s.kind === 'BUILD' && s.state !== 'DONE');
  if (noWebsite) {
    // A website the server could not describe (an older server, a site not read as the caller's): no card, the caller
    // keeps today's line.
    if (!presence && hasWebsite) return null;
    if (profiles > 0) return 'social';
    return presence ? 'noWebsite' : 'personal';
  }
  if (presence.platform === 'SHOPIFY') return 'shopify';
  if (presence.platform === 'WORDPRESS') return 'wordpress';
  if (presenceIsWix(presence.platform)) {
    const publish = stepState(presence, 'PUBLISH');
    return publish && publish !== 'DONE' ? 'wixUnpublished' : 'wix';
  }
  return 'external';
}

function visitsRow(presence: WebsitePresence, unpublished: boolean): SpaceCardRow | null {
  const open = act(copy(`${P}action.analytics`), { kind: 'analytics' }, 'space_card_analytics');
  const label = copy(`${P}cell.visits`);
  if (unpublished) return { ...row('visits', label, null, open), visits: { state: 'unpublished', count: 0, trend: null, daily: [] } };
  const visits = stripVisits(presence.visits, presenceIsWix(presence.platform));
  if (!visits) return null;
  return {
    ...row('visits', label, null, open),
    visits: { state: visits.known ? 'known' : 'unknown', count: visits.count, trend: visits.trend, daily: visits.daily },
  };
}

/** Domain: "Manage domain" with its own domain, "Connect" for one the account owns, else "Get {domain}" — the Domain page, never Wix. */
function domainRow(presence: WebsitePresence): SpaceCardRow {
  const label = copy(`${P}cell.domain`);
  const owned = (presence.ownedDomain ?? '').trim();
  const suggested = (presence.suggestedDomain ?? '').trim();
  if (presence.domainConnected === true) {
    return row(
      'domain',
      label,
      { text: (presence.displayHost ?? '').trim() },
      act(copy(`${C}action.manageDomain`), { kind: 'domain' }, 'space_card_manage_domain'),
    );
  }
  if (owned)
    return row(
      'domain',
      label,
      copy(`${P}domain.owned`, { domain: owned }),
      urlAction(copy(`${P}action.connect`), stepUrl(presence, 'DOMAIN'), 'space_card_connect_domain'),
    );
  const get = suggested ? copy(`${P}step.DOMAIN.actionSuggested`, { domain: suggested }) : copy(`${P}step.DOMAIN.action`);
  return row('domain', label, copy(`${P}host.freeWixAddress`), act(get, { kind: 'domain' }, 'space_card_get_domain'));
}

const MAILBOX_STATES = new Set(['NO_EMAIL', 'WIX_MAILBOX', 'EXTERNAL_MAILBOX']);

/** Business email: only once read, and only on the site's own domain. Connect opens the Space chat (or the inbox the server sent). */
function emailRow(presence: WebsitePresence): SpaceCardRow | null {
  const email = presence.businessEmail;
  if (presence.domainConnected !== true || !MAILBOX_STATES.has(email?.state ?? '')) return null;
  const label = copy(`${P}cell.email`);
  const inbox = presence.links?.inboxUrl;
  if (email?.state === 'NO_EMAIL') {
    const connect =
      urlAction(copy(`${P}action.connect`), inbox ?? '', 'space_card_connect_email') ??
      act(copy(`${P}action.connect`), { kind: 'chat', message: copy(`${P}prompt.email`) }, 'space_card_connect_email');
    return row('email', label, copy(`${P}email.none`), connect);
  }
  const address = (email?.address ?? '').trim();
  return row(
    'email',
    label,
    address ? { text: address } : copy(`${P}email.on`),
    urlAction(copy(`${P}action.openMail`), inbox ?? '', 'space_card_open_mail'),
  );
}

/**
 * The move to a Symphony site (WordPress, and any site with a REBUILD step): moved → "View new site"; ready →
 * "Compare"; else the `migrate` block — why, in one line, then "Migrate Site to Symphony" (the rebuild chat) as the
 * card's green button.
 */
function rebuildRow(presence: WebsitePresence, ctx: PresenceContext): SpaceCardRow | null {
  const migration = migrationState(presence);
  const label = copy(`${C}cell.symphonySite`);
  if (migration.state === 'SWITCHED') {
    return row(
      'rebuild',
      label,
      copy(`${P}onWix.moved`),
      urlAction(copy(`${P}action.viewNewSite`), migration.previewUrl, 'space_card_view_new_site'),
    );
  }
  if (migration.state === 'PREVIEW_READY') {
    return row(
      'rebuild',
      label,
      copy(`${P}step.REBUILD.titleReady`),
      urlAction(copy(`${P}step.REBUILD.actionCompare`), migration.previewUrl, 'space_card_compare'),
    );
  }
  return row(
    'migrate',
    label,
    copy(`${C}migrate.lead`),
    act(
      copy(`${C}action.migrateSite`),
      { kind: 'chat', message: copy(`${P}prompt.rebuild`, { url: siteRef(presence, ctx) }) },
      'space_card_rebuild',
      true,
    ),
  );
}

/**
 * Site · Connected apps · Calendar — the card of a Space with no website and no profile: "No site yet" with the green
 * "Build a Site in Symphony", how many apps are connected with a Connectors link, and — inside the Calendar release only
 * — whether a calendar is, with a Calendar link.
 */
function noWebsiteRows(name: string, account: SpaceCardAccount | null | undefined): SpaceCardRow[] {
  const apps = account?.connectedApps ?? null;
  const calendar = account?.calendarConnected ?? null;
  const rows: SpaceCardRow[] = [
    row(
      'website',
      copy(`${C}cell.website`),
      copy(`${C}site.none`),
      act(copy(`${C}action.buildSite`), { kind: 'chat', message: copy(`${P}prompt.build`, { name }) }, 'space_card_build_site', true),
    ),
    row(
      'apps',
      copy(`${C}cell.apps`),
      apps === null ? null : { count: apps },
      act(copy(`${C}action.connectors`), { kind: 'connectors', connector: '' }, 'space_card_connectors'),
    ),
  ];
  if (account?.calendarEnabled === true) {
    rows.push(
      row(
        'calendar',
        copy(`${C}cell.calendar`),
        calendar === null ? null : copy(calendar ? `${C}connection.connected` : `${C}connection.notConnected`),
        act(copy(`${C}cell.calendar`), { kind: 'calendar' }, 'space_card_calendar'),
      ),
    );
  }
  return rows;
}

/**
 * Orders · 30 days (Shopify), a count with no link: the number when it was read ("10K+" when Shopify stopped counting),
 * "—" when the read failed; no row when the server sent none (no Shopify connection to read through, or an older server).
 */
function ordersRow(presence: WebsitePresence): SpaceCardRow | null {
  const orders = presence.orders;
  if (!orders) return null;
  return row('orders', copy(`${C}cell.orders`), orders.known === true ? countOf(orders.orders30d, orders.atLeast === true) : { unknown: true }, null);
}

/**
 * Followers (social): the profile's followers when they were read; else null, and the card keeps its Connection row.
 * No "—" here: `known: false` is also every Instagram and TikTok profile, which would then never show anything else.
 */
function followersRow(stats: SpaceProfileStats | undefined): SpaceCardRow | null {
  return stats?.known === true ? row('followers', copy(`${C}cell.followers`), countOf(stats.followers), null) : null;
}

/** Posts · 30 days (social): the shown profile's posts when they were read; else no row. */
function postsRow(stats: SpaceProfileStats | undefined): SpaceCardRow | null {
  return stats?.postsKnown === true ? row('posts', copy(`${C}cell.posts`), countOf(stats.posts30d), null) : null;
}

/** Where each network shows its owner the profile's insights; a network not listed has no Engagement row. */
const INSIGHTS_URLS: Record<string, string> = {
  FACEBOOK: 'https://business.facebook.com/latest/insights',
  INSTAGRAM: 'https://business.facebook.com/latest/insights',
  PINTEREST: 'https://analytics.pinterest.com/',
  TIKTOK: 'https://www.tiktok.com/tiktokstudio/analytics',
};

/** Engagement (social): "Insights", the network's own insights page in a new tab. No number: no server sends one. */
function engagementRow(profile: SpaceCardProfile): SpaceCardRow | null {
  const insights = urlAction(copy(`${C}action.insights`), INSIGHTS_URLS[profile.provider] ?? '', 'space_card_insights');
  return insights ? row('engagement', copy(`${C}cell.engagement`), null, insights) : null;
}

/** A Space's profiles as the "Connected accounts" row (any card but social-only, which draws them across the top). */
function accountsRow(chips: SpaceCardChips): SpaceCardRow {
  return { ...row('accounts', copy(`${C}cell.accounts`), null, null), chips };
}

/** A social-only card's Connection row: the profile is connected; Connectors manages it. */
function socialConnectionRow(): SpaceCardRow {
  return row(
    'connection',
    copy(`${C}cell.connection`),
    copy(`${C}connection.connected`),
    act(copy(`${C}action.connectors`), { kind: 'connectors', connector: '' }, 'space_card_connectors'),
  );
}

/** Business email on a site not live yet: what was read, else "No business email" with Connect (the Space chat). */
function draftEmailRow(presence: WebsitePresence): SpaceCardRow {
  return (
    emailRow(presence) ??
    row(
      'email',
      copy(`${P}cell.email`),
      copy(`${P}email.none`),
      act(copy(`${P}action.connect`), { kind: 'chat', message: copy(`${P}prompt.email`) }, 'space_card_connect_email'),
    )
  );
}

/**
 * "Publish site" — the card's green button on a Wix site that is not live yet, and the Analytics page's button for it:
 * the PUBLISH step's url (the editor), else the Space chat asking to publish.
 */
export function spacePublishAction(presence: WebsitePresence, ctx: PresenceContext): SpaceCardAction {
  const publishUrl = stepUrl(presence, 'PUBLISH');
  return act(
    copy(`${C}action.publishSite`),
    publishUrl ? { kind: 'url', url: publishUrl } : { kind: 'chat', message: copy(`${P}prompt.publish`, { url: siteRef(presence, ctx) }) },
    'space_card_publish',
    true,
  );
}

/** A Wix site that is not live yet: the Analytics page shows no numbers for it, only "Publish your site to see visits". */
export function spaceSiteUnpublished(presence: WebsitePresence | null | undefined): boolean {
  return !!presence && spaceCardVariant(presence, 0, true) === 'wixUnpublished';
}

/** The platform's own admin: MOVE's `adminUrl`, PLATFORM_ADMIN's `url`, else the dashboard; '' once the site moved to Wix. */
function adminUrl(presence: WebsitePresence): string {
  if (migrationState(presence).state === 'SWITCHED') return '';
  const edit = presence.edit;
  if (edit?.mode === 'MOVE') return href(edit.adminUrl) || href(presence.dashboardUrl);
  if (edit?.mode === 'PLATFORM_ADMIN') return href(edit.url) || href(presence.dashboardUrl);
  return href(presence.dashboardUrl);
}

/**
 * The Space card, or null when the Space has a website the server did not describe (the caller keeps today's line).
 * `profiles` are the Space's social profiles and listings, in the header line's order (`presenceLinks` minus websites).
 * `selected`: the profile a social card shows (its chip was picked); absent or not a shown chip → the first.
 */
export function spaceCard(
  presence: WebsitePresence | null | undefined,
  profiles: SpaceCardProfile[],
  hasWebsite: boolean,
  ctx: PresenceContext,
  account?: SpaceCardAccount | null,
  selected?: string | null,
): SpaceCard | null {
  const variant = spaceCardVariant(presence, profiles.length, hasWebsite);
  if (!variant) return null;
  const shownProfiles = profiles.slice(0, SPACE_CARD_CHIP_LIMIT);
  const chips: SpaceCardChips | null =
    profiles.length > 0
      ? {
          shown: shownProfiles.map((p) => ({
            id: p.id,
            glyph: p.glyph,
            name: p.name,
            network: p.providerName,
            url: p.url,
            followers: followersOf(p.stats),
          })),
          more: Math.max(0, profiles.length - SPACE_CARD_CHIP_LIMIT),
          selected: null,
        }
      : null;
  const card: SpaceCard = {
    variant,
    name: presence ? siteName(presence, ctx) : ctx.spaceName.trim(),
    sub: null,
    glyph: null,
    chips: variant === 'social' ? chips : null,
    view: null,
    buttons: [],
    manage: variant !== 'personal',
    rows: [],
    footer: variant !== 'personal',
  };
  const push = (...actions: (SpaceCardAction | null)[]) => card.buttons.push(...actions.filter((a): a is SpaceCardAction => a !== null));

  if (variant === 'personal' || variant === 'noWebsite') {
    const email = (account?.email ?? '').trim();
    card.sub = email ? { text: email } : null;
    card.rows = noWebsiteRows(card.name, account);
    return card;
  }
  if (variant === 'social') {
    const shown = shownProfiles.find((p) => p.id === selected) ?? profiles[0];
    card.name = shown.name;
    card.sub = shown.providerName ? { text: shown.providerName } : null;
    card.glyph = shown.glyph;
    if (card.chips) card.chips = { ...card.chips, selected: shown.id };
    push(urlAction(copy(`${C}action.openProfile`), shown.url, 'space_card_open_profile'));
    // Followers when they were read, else the Connection row; then Posts · 30 days when read, and Engagement (the
    // network's insights) on a network that has them.
    card.rows = [followersRow(shown.stats) ?? socialConnectionRow(), postsRow(shown.stats), engagementRow(shown)].filter(
      (r): r is SpaceCardRow => r !== null,
    );
    return card;
  }

  const p = presence as WebsitePresence;
  const view = href(p.links?.viewUrl) || href(ctx.siteUrl);
  const host = (p.displayHost ?? '').trim() || address(view);
  card.sub = host ? { text: host } : null;
  const edit = fromStrip(stripEditAction(p, ctx));

  switch (variant) {
    case 'wix':
      card.view = urlAction(copy(`${C}action.viewSite`), view, 'space_card_view_site');
      push(edit);
      card.rows = [visitsRow(p, false), domainRow(p), emailRow(p)].filter((r): r is SpaceCardRow => r !== null);
      break;
    case 'wixUnpublished': {
      card.view = urlAction(copy(`${C}action.preview`), view, 'space_card_preview');
      push(edit);
      const publish = spacePublishAction(p, ctx);
      card.rows = [row('site', copy(`${P}cell.site`), copy(`${C}site.notPublished`), publish), visitsRow(p, true), domainRow(p), draftEmailRow(p)].filter(
        (r): r is SpaceCardRow => r !== null,
      );
      break;
    }
    case 'shopify': {
      // ONE Shopify button: the admin, else the store. Orders · 30 days first (a number only), then Connection. No
      // Revenue row.
      push(urlAction(copy(`${C}action.openInShopify`), adminUrl(p) || view, 'space_card_open_shopify'));
      card.sub = host ? { text: `${host} · Shopify` } : { text: 'Shopify' };
      const connect = stepState(p, 'CONNECT');
      card.rows = [
        ordersRow(p),
        row(
          'connection',
          copy(`${C}cell.connection`),
          connect === null ? null : copy(connect === 'DONE' ? `${C}connection.connected` : `${C}connection.notConnected`),
          // The Connectors page's own id for the Shopify entry, so the link lands on it (`ShopifyConnectionHealth`).
          act(copy(`${C}action.shopifyConnector`), { kind: 'connectors', connector: 'shopify-direct' }, 'space_card_shopify_connector'),
        ),
      ].filter((r): r is SpaceCardRow => r !== null);
      break;
    }
    case 'wordpress':
      // Named by its domain, "WordPress detected" under it. No WordPress connector: no "Connect WordPress" and no Visits
      // row; the move to Symphony is the card's only row.
      card.name = host || card.name;
      card.sub = copy(`${C}site.wordpressDetected`);
      card.view = urlAction(copy(`${C}action.viewSite`), view, 'space_card_view_site');
      push(urlAction(copy(`${C}action.openWordPress`), adminUrl(p), 'space_card_open_wordpress'));
      card.rows = [rebuildRow(p, ctx)].filter((r): r is SpaceCardRow => r !== null);
      break;
    case 'external':
      card.view = urlAction(copy(`${C}action.viewSite`), view, 'space_card_view_site');
      push(edit);
      card.rows = [visitsRow(p, false), (p.steps ?? []).some((s) => s.kind === 'REBUILD') ? rebuildRow(p, ctx) : null].filter(
        (r): r is SpaceCardRow => r !== null,
      );
      break;
  }
  if (chips) card.rows.push(accountsRow(chips));
  return card;
}

/**
 * The Space card for a Space, from what the header already reads: its `presence`, its website tile and its bindings.
 * The profiles are the header line's non-website links, each with its binding's stats.
 */
export function spaceCardFor(
  presence: WebsitePresence | null | undefined,
  website: WorkspaceWebsite | null | undefined,
  bindings: SpaceBindingsResponse | undefined,
  ctx: PresenceContext,
  account?: SpaceCardAccount | null,
  selected?: string | null,
): SpaceCard | null {
  const links = presenceLinks(website, bindings);
  const stats = new Map(
    (bindings?.resources ?? [])
      .filter((r) => r.kind === 'SOCIAL_PROFILE' && (r.bindingId ?? '') !== '')
      .map((r) => [r.bindingId as string, r.stats] as const),
  );
  const profiles: SpaceCardProfile[] = links
    .filter((l) => !l.main && stats.has(l.bindingId))
    .map((l) => {
      const s = stats.get(l.bindingId);
      return s ? { ...l, stats: s } : l;
    });
  return spaceCard(presence, profiles, links.some((l) => l.main), ctx, account, selected);
}
